import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Space,
  Switch,
  Tag,
  Typography,
  message
} from 'antd';
import { CloudUploadOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { configAPI } from '../services/api';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const formLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 }
};

function normalizeForForm(config = {}) {
  return {
    enabled: Boolean(config.enabled),
    versionName: config.versionName || '',
    versionCode: Number(config.versionCode || 1),
    forceUpdate: Boolean(config.forceUpdate),
    title: config.title || '',
    message: config.message || '',
    downloadUrl: config.downloadUrl || '',
    resourceUrl: config.resourceUrl || '',
    changelog: config.changelog || '',
    extra: config.extra || {}
  };
}

function UpdateStatusTag({ config }) {
  if (!config?.enabled) {
    return <Tag>未启用</Tag>;
  }
  return <Tag color={config.forceUpdate ? 'red' : 'green'}>{config.forceUpdate ? '强制更新' : '正常更新'}</Tag>;
}

function ClientUpdateManagement() {
  const [loading, setLoading] = useState(false);
  const [savingTarget, setSavingTarget] = useState('');
  const [configs, setConfigs] = useState({ sdk: null, apk: null });
  const [sdkForm] = Form.useForm();
  const [apkForm] = Form.useForm();

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await configAPI.getClientUpdates();
      const data = response.data?.data || {};
      setConfigs(data);
      sdkForm.setFieldsValue(normalizeForForm(data.sdk));
      apkForm.setFieldsValue(normalizeForForm(data.apk));
    } catch (error) {
      message.error('加载版本配置失败');
    } finally {
      setLoading(false);
    }
  }, [apkForm, sdkForm]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const saveConfig = async (target, form) => {
    setSavingTarget(target);
    try {
      const values = await form.validateFields();
      const response = await configAPI.updateClientUpdate(target, values);
      setConfigs((prev) => ({ ...prev, [target]: response.data?.data }));
      message.success(target === 'sdk' ? 'SDK 更新配置已保存' : 'APK 更新配置已保存');
    } catch (error) {
      if (!error?.errorFields) {
        message.error('保存版本配置失败');
      }
    } finally {
      setSavingTarget('');
    }
  };

  return (
    <div className="page-shell">
      <div className="page-header">
        <div className="page-header-main">
          <div className="page-title">版本更新</div>
          <div className="page-subtitle">分开维护 SDK 远程配置和 APK 整包更新，客户端启动后会自动读取这里的配置。</div>
        </div>
        <div className="page-header-actions">
          <Button icon={<ReloadOutlined />} onClick={loadConfigs} loading={loading}>
            刷新
          </Button>
        </div>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={12}>
          <Card
            title={
              <Space>
                <CloudUploadOutlined />
                <span>SDK 远程更新</span>
                <UpdateStatusTag config={configs.sdk} />
              </Space>
            }
            className="section-card"
          >
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="SDK 更新不替换 APK，只刷新 SDK 网页、菜单入口和资源地址。Java 原生代码变化仍需要走 APK 更新。"
            />
            <Form form={sdkForm} layout="horizontal" {...formLayout}>
              <Form.Item label="启用更新" name="enabled" valuePropName="checked">
                <Switch checkedChildren="启用" unCheckedChildren="关闭" />
              </Form.Item>
              <Form.Item label="SDK版本名" name="versionName" rules={[{ required: true, message: '请输入版本名' }]}>
                <Input placeholder="例如 1.0.1" />
              </Form.Item>
              <Form.Item label="SDK版本号" name="versionCode" rules={[{ required: true, message: '请输入版本号' }]}>
                <InputNumber min={1} precision={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label="强制刷新" name="forceUpdate" valuePropName="checked">
                <Switch checkedChildren="强制" unCheckedChildren="普通" />
              </Form.Item>
              <Form.Item label="提示标题" name="title">
                <Input placeholder="SDK update" />
              </Form.Item>
              <Form.Item label="提示内容" name="message">
                <TextArea rows={2} placeholder="可选，SDK 配置更新提示" />
              </Form.Item>
              <Form.Item label="资源地址" name="resourceUrl">
                <Input placeholder="可选，例如 http://域名/sdk/remote.json" />
              </Form.Item>
              <Form.Item label="后台根地址" name={['extra', 'baseUrl']}>
                <Input placeholder="https://www.xrzzaoz.com" />
              </Form.Item>
              <Form.Item label="登录地址" name={['extra', 'loginUrl']}>
                <Input placeholder="https://www.xrzzaoz.com/api/login/browser-login" />
              </Form.Item>
              <Form.Item label="充值地址" name={['extra', 'shopUrl']}>
                <Input placeholder="https://www.xrzzaoz.com/shop.html" />
              </Form.Item>
              <Form.Item label="福利地址" name={['extra', 'giftUrl']}>
                <Input placeholder="https://www.xrzzaoz.com/gift-claim.html" />
              </Form.Item>
              <Form.Item label="抽奖地址" name={['extra', 'lotteryUrl']}>
                <Input placeholder="https://www.xrzzaoz.com/lottery.html" />
              </Form.Item>
              <Form.Item label="用户中心地址" name={['extra', 'userCenterUrl']}>
                <Input placeholder="https://www.xrzzaoz.com/user-center.html" />
              </Form.Item>
              <Form.Item label="检测间隔" name={['extra', 'checkIntervalMinutes']}>
                <InputNumber min={5} precision={0} addonAfter="分钟" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label="更新说明" name="changelog">
                <TextArea rows={4} placeholder="SDK 更新说明" />
              </Form.Item>
              <Form.Item wrapperCol={{ span: 18, offset: 6 }}>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={savingTarget === 'sdk'}
                  onClick={() => saveConfig('sdk', sdkForm)}
                >
                  保存 SDK 配置
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} xl={12}>
          <Card
            title={
              <Space>
                <CloudUploadOutlined />
                <span>APK 整包更新</span>
                <UpdateStatusTag config={configs.apk} />
              </Space>
            }
            className="section-card"
          >
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="APK 更新会在客户端弹窗提示，用户点击后打开下载地址。版本号必须大于当前 APK 的 versionCode 才会提示。"
            />
            <Form form={apkForm} layout="horizontal" {...formLayout}>
              <Form.Item label="启用更新" name="enabled" valuePropName="checked">
                <Switch checkedChildren="启用" unCheckedChildren="关闭" />
              </Form.Item>
              <Form.Item label="APK版本名" name="versionName" rules={[{ required: true, message: '请输入版本名' }]}>
                <Input placeholder="例如 1.3.846" />
              </Form.Item>
              <Form.Item label="APK版本号" name="versionCode" rules={[{ required: true, message: '请输入版本号' }]}>
                <InputNumber min={1} precision={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label="强制更新" name="forceUpdate" valuePropName="checked">
                <Switch checkedChildren="强制" unCheckedChildren="普通" />
              </Form.Item>
              <Form.Item label="弹窗标题" name="title">
                <Input placeholder="发现新版本" />
              </Form.Item>
              <Form.Item label="弹窗内容" name="message">
                <TextArea rows={3} placeholder="检测到新版本，请更新后继续游戏。" />
              </Form.Item>
              <Form.Item label="下载地址" name="downloadUrl" rules={[{ required: true, message: '请输入 APK 下载地址' }]}>
                <Input placeholder="https://www.xrzzaoz.com/downloads/xurizhicheng.apk" />
              </Form.Item>
              <Form.Item label="包名" name={['extra', 'packageName']}>
                <Input placeholder="com.camelgames.aoz.mrjx" />
              </Form.Item>
              <Form.Item label="检测间隔" name={['extra', 'checkIntervalMinutes']}>
                <InputNumber min={5} precision={0} addonAfter="分钟" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label="更新说明" name="changelog">
                <TextArea rows={5} placeholder="APK 更新说明，客户端弹窗会一起显示" />
              </Form.Item>
              <Form.Item wrapperCol={{ span: 18, offset: 6 }}>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={savingTarget === 'apk'}
                  onClick={() => saveConfig('apk', apkForm)}
                >
                  保存 APK 配置
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>

      <Card className="section-card" title="客户端检测接口">
        <Space direction="vertical" size={4}>
          <Text code>/api/configs/public/client-update</Text>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            APK 会带上当前 APK 版本号、SDK 版本号和包名访问此接口。后台返回 SDK 配置和 APK 更新信息，两套版本互不影响。
          </Paragraph>
        </Space>
      </Card>
    </div>
  );
}

export default ClientUpdateManagement;
